import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import http from "../../services/http";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

/* ─── CSV export ─── */
const downloadCSV = (filename, rows, headers) => {
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map(escape).join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const dirBadge = (dir) => (
  <span style={{
    ...ts.badge,
    background: dir === "credit" ? "#dcfce7" : "#fee2e2",
    color:      dir === "credit" ? "#16a34a" : "#dc2626",
  }}>
    {dir === "credit" ? "▲ Credit" : "▼ Debit"}
  </span>
);

const statusBadge = (s = "") => {
  const map = { approved: ["#dcfce7","#16a34a"], rejected: ["#fee2e2","#dc2626"], pending: ["#fef9c3","#92400e"] };
  const [bg, color] = map[s.toLowerCase()] || ["#f1f5f9","#64748b"];
  return <span style={{ ...ts.badge, background: bg, color }}>{s || "—"}</span>;
};

const TABS = ["Ledger", "Deposits", "Withdrawals"];

export const UserWalletHistoryModal = ({ show, onHide, user }) => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab,     setTab]     = useState(0);
  const [search,  setSearch]  = useState("");

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await http.get("user_wallet_history", { params: { user_id: user.id } });
      if (res.data?.status) setData(res.data);
      else toast.error(res.data?.msg || "Failed to load wallet history");
    } catch { toast.error("Server error"); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { if (show) { setTab(0); setSearch(""); load(); } }, [show, load]);

  const rows = (() => {
    if (!data) return [];
    const src = tab === 0 ? data.ledger : tab === 1 ? data.deposits : data.withdrawals;
    if (!search.trim()) return src;
    const v = search.toLowerCase();
    return src.filter((r) =>
      (r.ref_type || r.type || "").toLowerCase().includes(v) ||
      (r.remark || "").toLowerCase().includes(v) ||
      (r.status || "").toLowerCase().includes(v) ||
      String(r.amount).includes(v)
    );
  })();

  const handleExport = () => {
    if (!rows.length) { toast.warn("Nothing to export"); return; }
    const headers = tab === 0
      ? ["id","ref_type","amount","balance_before","balance_after","txn_direction","remark","created_at"]
      : tab === 1
      ? ["id","amount","status","utr_number","created_at"]
      : ["id","amount","status","created_at"];
    downloadCSV(`Wallet_${TABS[tab]}_${user?.name}_${new Date().toISOString().slice(0,10)}.csv`, rows, headers);
    toast.success("CSV exported");
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
      <div style={ms.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={ms.iconWrap}>
            <Icon icon="solar:wallet-bold-duotone" style={{ fontSize: 20 }} />
          </div>
          <div>
            <div style={ms.title}>Wallet History</div>
            {data?.user_info && (
              <div style={ms.sub}>
                {data.user_info.name} · {data.user_info.mobile} ·{" "}
                <strong style={{ color: "#16a34a" }}>Balance: {fmt(data.user_info.wallet_balance)}</strong>
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={ms.exportBtn} onClick={handleExport}>
            <Icon icon="solar:file-download-bold" style={{ fontSize: 14 }} />Export
          </button>
          <button style={ms.closeBtn} onClick={onHide}>
            <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
          </button>
        </div>
      </div>

      <div style={ms.body}>
        {/* Tabs */}
        <div style={ms.tabRow}>
          {TABS.map((t, i) => (
            <button key={t} style={{ ...ms.tabBtn, ...(tab === i ? ms.tabActive : {}) }} onClick={() => setTab(i)}>
              {t}
              <span style={{ ...ms.tabCount, background: tab === i ? "rgba(255,255,255,0.25)" : "#f1f5f9" }}>
                {data ? (i === 0 ? data.ledger?.length : i === 1 ? data.deposits?.length : data.withdrawals?.length) : 0}
              </span>
            </button>
          ))}
          <div style={{ marginLeft: "auto", position: "relative" }}>
            <Icon icon="ion:search-outline" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#94a3b8", pointerEvents: "none" }} />
            <input style={{ ...ms.searchInput, paddingLeft: 30 }} placeholder="Filter…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        {loading ? (
          <div style={ms.emptyCell}>
            <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} />
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div style={ms.emptyCell}>No records found</div>
        ) : (
          <div style={ms.tableWrap}>
            <table style={ms.table}>
              <thead>
                <tr>
                  {tab === 0 && <>
                    <th style={ms.th}>#</th>
                    <th style={ms.th}>Type</th>
                    <th style={ms.th}>Direction</th>
                    <th style={{ ...ms.th, textAlign: "right" }}>Amount</th>
                    <th style={{ ...ms.th, textAlign: "right" }}>Balance Before</th>
                    <th style={{ ...ms.th, textAlign: "right" }}>Balance After</th>
                    <th style={ms.th}>Remark</th>
                    <th style={ms.th}>Date</th>
                  </>}
                  {tab === 1 && <>
                    <th style={ms.th}>#</th>
                    <th style={{ ...ms.th, textAlign: "right" }}>Amount</th>
                    <th style={ms.th}>UTR</th>
                    <th style={ms.th}>Status</th>
                    <th style={ms.th}>Date</th>
                  </>}
                  {tab === 2 && <>
                    <th style={ms.th}>#</th>
                    <th style={{ ...ms.th, textAlign: "right" }}>Amount</th>
                    <th style={ms.th}>Status</th>
                    <th style={ms.th}>Date</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}
                  >
                    {tab === 0 && <>
                      <td style={ms.td}><span style={ts.serial}>{idx + 1}</span></td>
                      <td style={ms.td}><span style={ts.refType}>{row.ref_type || "—"}</span></td>
                      <td style={ms.td}>{dirBadge(row.txn_direction)}</td>
                      <td style={{ ...ms.td, textAlign: "right" }}>
                        <span style={{ fontWeight: 700, color: row.txn_direction === "credit" ? "#16a34a" : "#dc2626" }}>
                          {row.txn_direction === "credit" ? "+" : "-"}{fmt(row.amount)}
                        </span>
                      </td>
                      <td style={{ ...ms.td, textAlign: "right" }}><span style={ts.mono}>{fmt(row.balance_before)}</span></td>
                      <td style={{ ...ms.td, textAlign: "right" }}><span style={{ ...ts.mono, fontWeight: 700 }}>{fmt(row.balance_after)}</span></td>
                      <td style={ms.td}><span style={{ fontSize: "0.78rem", color: "#64748b" }}>{row.remark || "—"}</span></td>
                      <td style={ms.td}><span style={ts.date}>{row.created_at}</span></td>
                    </>}
                    {tab === 1 && <>
                      <td style={ms.td}><span style={ts.serial}>{idx + 1}</span></td>
                      <td style={{ ...ms.td, textAlign: "right" }}><span style={{ fontWeight: 700, color: "#16a34a" }}>{fmt(row.amount)}</span></td>
                      <td style={ms.td}><span style={ts.mono}>{row.utr_number || "—"}</span></td>
                      <td style={ms.td}>{statusBadge(row.status)}</td>
                      <td style={ms.td}><span style={ts.date}>{row.created_at}</span></td>
                    </>}
                    {tab === 2 && <>
                      <td style={ms.td}><span style={ts.serial}>{idx + 1}</span></td>
                      <td style={{ ...ms.td, textAlign: "right" }}><span style={{ fontWeight: 700, color: "#dc2626" }}>{fmt(row.amount)}</span></td>
                      <td style={ms.td}>{statusBadge(row.status)}</td>
                      <td style={ms.td}><span style={ts.date}>{row.created_at}</span></td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
};

const ms = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "18px 24px 14px", borderBottom: "1px solid #f1f5f9" },
  iconWrap: { width: 40, height: 40, borderRadius: 10, background: "linear-gradient(135deg,#16632b,#1da144)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  title: { fontWeight: 800, fontSize: "1.02rem", color: "#0f172a" },
  sub:   { fontSize: "0.78rem", color: "#64748b", marginTop: 2 },
  exportBtn: { display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 8, color: "#fff", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  closeBtn: { background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" },
  body: { padding: "16px 24px 20px" },
  tabRow: { display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" },
  tabBtn: { display: "flex", alignItems: "center", padding: "7px 14px", border: "1.5px solid #e2e8f0", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: "0.8rem", fontWeight: 600, color: "#475569", fontFamily: "inherit" },
  tabActive: { background: "linear-gradient(135deg,#0f4c25,#16632b)", color: "#fff", border: "none" },
  tabCount: { marginLeft: 7, padding: "2px 7px", borderRadius: 100, fontSize: "0.67rem", fontWeight: 700 },
  searchInput: { padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: "0.8rem", color: "#334155", background: "#f8fafc", outline: "none", fontFamily: "inherit", width: 160 },
  emptyCell: { padding: "40px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },
  tableWrap: { overflowX: "auto", maxHeight: "55vh", overflowY: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" },
  th: { padding: "9px 12px", background: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: "0.69rem", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1 },
  td: { padding: "8px 12px", borderBottom: "1px solid #f8fafc", color: "#334155", verticalAlign: "middle" },
};

const ts = {
  badge: { display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 100, fontSize: "0.68rem", fontWeight: 700 },
  serial: { fontSize: "0.73rem", color: "#94a3b8" },
  refType: { fontFamily: "monospace", fontSize: "0.75rem", color: "#475569", fontWeight: 600 },
  mono: { fontFamily: "monospace", fontSize: "0.78rem", color: "#475569" },
  date: { fontSize: "0.75rem", color: "#64748b" },
};
